/**
 * OrganizerLogo Component
 * Displays the challenge organizer's logo or company name
 */

import React, { memo, useMemo } from 'react'
import { Typography } from '@material-ui/core'
import { GoalWithProgress } from '../../../services/api.types'
import { useChallengeInfoStyles, getLogoStyles } from '../ChallengeInfo.styles'

interface OrganizerLogoProps {
  goal: GoalWithProgress
}

/**
 * Renders the organizer logo or company name if no logo is available
 * Handles both SVG and raster image formats with appropriate styling
 */
const OrganizerLogo: React.FC<OrganizerLogoProps> = memo(({ goal }) => {
  const classes = useChallengeInfoStyles()
  const { logoUrl, companyLogo, companyName } = goal

  // Determine which logo to use (custom logo URL or company logo)
  const logo = logoUrl || companyLogo

  // Memoize logo style calculation
  const logoStyle = useMemo(() => {
    if (!logo) return null
    const isSvg = logo.endsWith('.svg')
    return getLogoStyles(isSvg)
  }, [logo])

  // Fallback to company name if no logo
  if (!logo) {
    return (
      <div>
        <Typography variant="caption">{companyName}</Typography>
      </div>
    )
  }

  return (
    <div>
      <img className={classes.logo} style={logoStyle || undefined} src={logo} alt={companyName} />
    </div>
  )
})

OrganizerLogo.displayName = 'OrganizerLogo'

export default OrganizerLogo
